import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { listenToAllChannels, listenToNotifications } from '../firebase/firebaseFunctions';

const useUnreadMessages = () => {
  const { t } = useTranslation();
  const authData = useSelector(state => state?.auth?.data);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const chatCountRef = useRef(0);
  const channelCountRef = useRef(0);
  const previousTotalRef = useRef(0);
  const isInitializedRef = useRef(false);
  const previousChatCountRef = useRef(0);
  const previousChannelCountRef = useRef(0);
  const lastNotificationRef = useRef(null);

  const userId = authData?.userId;

  const showToast = useCallback(
    ({ title, message, type = 'chat', count = 1, senderImage = null, providerName = null }) => {
      const icon = type === 'channel' ? '📢' : '💬';
      const typeLabel = type === 'channel' ? t('CHAT.channel') : t('CHAT.chat');
      const countText = count > 1 ? ` (${count} ${t('CHAT.newMessages').toLowerCase()})` : '';

      const avatarHtml = senderImage
        ? `<img src="${senderImage}" style="width: 48px; height: 48px; border-radius: 50%; object-fit: cover; border: 2px solid #E6616A;" alt="" />`
        : `<div style="font-size: 32px;">${icon}</div>`;

      const toast = document.createElement('div');
      toast.innerHTML = `
      <div style="display: flex; align-items: start; gap: 12px;">
        ${avatarHtml}
        <div style="flex: 1;">
          <div style="font-weight: 600; margin-bottom: 4px; font-size: 15px;">${title}</div>
          <div style="font-size: 13px; color: #6b7280; margin-bottom: 6px; line-height: 1.4; max-height: 60px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${message}${countText}</div>
          <div style="font-size: 12px; color: #9ca3af; display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
            <span style="background: #E6616A; color: white; padding: 2px 8px; border-radius: 4px; font-weight: 500;">${typeLabel}</span>
            ${providerName ? `<span>• ${providerName}</span>` : ''}
          </div>
        </div>
      </div>
    `;
      toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: white;
      color: #1f2937;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
      z-index: 9999;
      cursor: pointer;
      animation: slideIn 0.3s ease-out;
      min-width: 300px;
      border-left: 4px solid #E6616A;
    `;

      const style = document.createElement('style');
      style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
      if (!document.getElementById('toast-styles')) {
        style.id = 'toast-styles';
        document.head.appendChild(style);
      }

      toast.onclick = () => {
        window.location.href = '/chat';
      };

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 300);
      }, 5000);
    },
    [t]
  );

  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      const playBeep = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = audioContext.currentTime;
      playBeep(800, now, 0.15);
      playBeep(1000, now + 0.15, 0.15);
    } catch (err) {
      // Silent error
    }
  }, []);

  useEffect(() => {
    window.testChatNotification = () => {
      playNotificationSound();
    };

    return () => {
      delete window.testChatNotification;
    };
  }, [playNotificationSound]);

  useEffect(() => {
    if (!userId) {
      setHasUnreadMessages(false);
      return;
    }

    const updateTotalCount = () => {
      const total = chatCountRef.current + channelCountRef.current;

      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        previousTotalRef.current = total;
        previousChatCountRef.current = chatCountRef.current;
        previousChannelCountRef.current = channelCountRef.current;
      } else {
        previousTotalRef.current = total;
        previousChatCountRef.current = chatCountRef.current;
        previousChannelCountRef.current = channelCountRef.current;
      }

      setHasUnreadMessages(total > 0);
    };

    const unsubscribeNotifications = listenToNotifications(userId, newNotifications => {
      const unreadChats = newNotifications?.unreadChats || [];
      const previousChatCount = chatCountRef.current;
      chatCountRef.current = unreadChats.length;

      if (chatCountRef.current > previousChatCount && isInitializedRef.current) {
        const sortedChats = [...unreadChats].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const latestMessage = sortedChats[0];

        if (latestMessage && latestMessage.message) {
          const messageInfo = latestMessage.message;

          const notificationId = latestMessage.chatId + '_' + (messageInfo.timestamp || latestMessage.timestamp);

          const isActiveChat = window.activeChatId && latestMessage.chatId === window.activeChatId;

          if (notificationId !== lastNotificationRef.current && !isActiveChat) {
            lastNotificationRef.current = notificationId;

            playNotificationSound();

            showToast({
              title: messageInfo.senderName || t('CHAT.newMessage'),
              message: messageInfo.content || t('CHAT.youReceivedMessage'),
              type: messageInfo.type || 'chat',
              count: chatCountRef.current - previousChatCount,
              senderImage: messageInfo.senderImage,
              providerName: messageInfo.providerName,
            });
          }
        } else {
          playNotificationSound();
          showToast({
            title: t('CHAT.newMessage'),
            message: t('CHAT.youReceivedMessage'),
            type: 'chat',
            count: chatCountRef.current - previousChatCount,
          });
        }
      }

      updateTotalCount();
    });

    const unsubscribeChannels = listenToAllChannels(userId, unreadChannels => {
      const previousCount = channelCountRef.current;
      channelCountRef.current = unreadChannels.length;

      if (unreadChannels.length > previousCount && isInitializedRef.current && unreadChannels.length > 0) {
        const sortedChannels = [...unreadChannels].sort((a, b) => {
          const timeA = new Date(a.lastMessage?.createdAt || 0).getTime();
          const timeB = new Date(b.lastMessage?.createdAt || 0).getTime();
          return timeB - timeA;
        });

        const latestChannel = sortedChannels[0];
        const channelId = latestChannel.id + '_' + latestChannel.lastMessage?.createdAt;

        const isActiveChannel = window.activeChatId && latestChannel.id === window.activeChatId;

        if (channelId !== lastNotificationRef.current && !isActiveChannel) {
          lastNotificationRef.current = channelId;

          playNotificationSound();

          showToast({
            title: latestChannel.name || t('CHAT.newMessageInChannel'),
            message: latestChannel.lastMessage?.content || t('CHAT.someonePostedInChannel'),
            type: 'channel',
            count: unreadChannels.length - previousCount,
            senderImage: latestChannel.lastMessage?.senderImage,
            providerName: latestChannel.lastMessage?.providerName,
          });
        }
      }

      updateTotalCount();
    });

    return () => {
      if (typeof unsubscribeNotifications === 'function') {
        unsubscribeNotifications();
      }
      if (typeof unsubscribeChannels === 'function') {
        unsubscribeChannels();
      }
    };
  }, [userId, showToast, playNotificationSound, t]);

  return { hasUnreadMessages };
};

export default useUnreadMessages;
